import type { FC } from 'react'
import React, { useMemo, memo } from 'react'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import { ResponsiveBar } from '@nivo/bar'
import CustomBar from '../CustomBar'
import ScreenshotButton from '../ScreenshotButton'
import ChartLegend from '../ChartLegend'

import { isEqual } from '@homeviews/utils'
import { isEngageUser, useGetOwnerReviewsQuery } from '@homeviews/store'
import { chartTheme } from '../../helpers/layout'
import { transformOwnerReviewsData } from '../../helpers/transform'
import { Spinner, Typography, Tooltip } from '@homeviews/shared-ui'
import NoData from '../NoData'
import { CHART_BRAND_COLORS } from '../../helpers/settings'
import { useSelector } from 'react-redux'

const useStyles = makeStyles(() =>
  createStyles({
    root: {},
    title: {
      marginBottom: 16,
    },
  }),
)

interface IProps {
  activeFilters: any
  graphDisabled
  gridRef?: HTMLElement
  filtersDetails: any
  overallRatingHistoryChart: any
}

const AllDataLayer =
  ({ max }) =>
  (layerProps) => {
    return layerProps.bars.map((bar) => {
      try {
        return (
          <>
            <rect
              width={bar?.width || 0}
              height={layerProps.innerHeight}
              rx={10}
              ry={10}
              y={layerProps.yScale(max)}
              x={bar.x}
              fill="#DCDFE3"
            />
            <text
              x={bar.x + bar.width / 2}
              y="20"
              textAnchor="middle"
              dominantBaseline="central"
              fillOpacity="1"
              style={{
                color: '#4E516B',
                fontSize: '14px',
                fontWeight: 500,
                letterSpacing: 0,
                lineHeight: '16px',
              }}
            >
              {max}
            </text>
          </>
        )
      } catch {
        return null
      }
    })
  }

const OwnerReviewsChart: FC<IProps> = memo(
  ({
    activeFilters,
    graphDisabled,
    gridRef,
    filtersDetails,
    overallRatingHistoryChart,
  }) => {
    const classes = useStyles()
    const isEngagePackage = Boolean(useSelector(isEngageUser))

    const { data: ownerReviews, isFetching: isFetchingOwnerReviews } =
      useGetOwnerReviewsQuery(
        {
          'reviewer_type[]': ['owner', 'shared_owner'],
          aggregate: 'count',
          aggregate_fieldset: 'owner_types',
          ...activeFilters,
          engage: isEngagePackage,
        },
        {
          skip: graphDisabled,
        },
      )

    const chartData: any = useMemo(
      () => transformOwnerReviewsData(ownerReviews?.data?.[0]),
      [ownerReviews?.data],
    )

    const MAX_DATA = chartData?.all ? Number(chartData.all) : 0

    if (!chartData || MAX_DATA === 0) {
      if (gridRef) {
        gridRef.style.display = 'none'
      }
      if (overallRatingHistoryChart?.style) {
        overallRatingHistoryChart.classList.add('h-chartFullWidth')
      }
      return <NoData />
    } else {
      if (gridRef) {
        gridRef.style.display = 'block'
      }
      if (overallRatingHistoryChart?.style) {
        overallRatingHistoryChart.classList.remove('h-chartFullWidth')
      }
    }

    if (isFetchingOwnerReviews) {
      return <Spinner centered />
    }

    return (
      <>
        <div className="h-exportableDataTitle">
          <Typography variant="h3" className={classes.title}>
            Owner Reviews
          </Typography>
          <ScreenshotButton />
          <Tooltip
            title="Owner Reviews"
            text="Discover how many out of your total owner reviews bought directly from you, used Help to Buy or were a First Time Buyer."
          />
        </div>

        {filtersDetails && filtersDetails}

        <div className="l-chartContainer">
          <div className="l-chartWrapper">
            <ResponsiveBar
              data={[chartData]}
              groupMode="grouped"
              barComponent={(barProps) => (
                <CustomBar
                  {...barProps}
                  barLayout="ownerReviews"
                  shouldRenderLabel
                />
              )}
              keys={['ftb', 'htb', 'nbb', 'all']}
              initialHiddenIds={['all']}
              colors={[
                CHART_BRAND_COLORS.green1,
                CHART_BRAND_COLORS.gold2,
                CHART_BRAND_COLORS.darkBlue3,
              ]}
              theme={chartTheme}
              animate={false}
              isInteractive={false}
              enableGridX={false}
              axisTop={null}
              axisRight={null}
              axisBottom={null}
              axisLeft={{
                tickSize: 0,
              }}
              margin={{ top: 50, right: 20, bottom: 40, left: 30 }}
              maxValue={MAX_DATA}
              minValue={0}
              innerPadding={30}
              borderRadius={10}
              labelTextColor="#FFFFFF"
              layers={[
                'grid',
                'axes',
                AllDataLayer({ max: MAX_DATA }),
                'bars',
                'markers',
                'legends',
                'annotations',
              ]}
            />
          </div>
          <ChartLegend
            data={[
              {
                text: 'FTB',
                shapeColor: CHART_BRAND_COLORS.green1,
              },
              {
                text: 'HTB',
                shapeColor: CHART_BRAND_COLORS.gold2,
              },
              {
                text: 'New Build Buyer',
                shapeColor: CHART_BRAND_COLORS.darkBlue3,
              },
            ]}
          />
        </div>
      </>
    )
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
)

export default OwnerReviewsChart
